// NoMatch 真实 API 接口 - 集成 SignalR 即时通讯
import { HubConnectionBuilder, LogLevel, HubConnectionState } from '@microsoft/signalr';
import { getToken, removeToken } from '../utils/Auth';
import axios from 'axios';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';

// const API_BASE_URL ='https://localhost:7137/api/stemcellsoft';
const API_BASE_URL = 'https://mulproject2-cxg8bcb5abhbgaf0.westus2-01.azurewebsites.net/api/stemcellsoft';
// const SIGNALR_HUB_URL = `https://localhost:7137/nomatchhub`;
const SIGNALR_HUB_URL = `https://mulproject2-cxg8bcb5abhbgaf0.westus2-01.azurewebsites.net/nomatchhub`;

// 配置 axios 默認設置
axios.defaults.withCredentials = false;
axios.defaults.headers.common['Content-Type'] = 'application/json';
// 移除靜態的 Authorization header 設定
// axios.defaults.headers.common["Authorization"] = `Bearer ${getToken()}`;
axios.defaults.timeout = 10000; // 10秒超時

// 請求攔截器
axios.interceptors.request.use(
  (config) => {
    // 動態添加 JWT token
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 可以在這裡添加認證 token
    console.log('API Request:', config.method?.toUpperCase(), config.url, token ? 'with token' : 'no token');
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// 響應攔截器
axios.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    // 統一錯誤處理
    if (error.response) {
      // 服務器響應了錯誤狀態碼
      console.error('Response Error:', error.response.status, error.response.data);
      switch (error.response.status) {
        case 401:
          console.error('未授權訪問 - Token 可能已過期');
          // 清除可能已過期的 token
          removeToken();
          break;
        case 403:
          console.error('禁止訪問');
          break;
        case 404:
          console.error('資源未找到');
          break;
        case 500:
          console.error('服務器內部錯誤');
          break;
        default:
          console.error('請求失敗:', error.response.data?.message || error.message);
      }
    } else if (error.request) {
      // 請求已發送但沒有收到響應
      console.error('Network Error:', error.request);
    } else {
      // 其他錯誤
      console.error('Request Setup Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// SignalR 连接管理类
class SignalRManager {
  constructor() {
    this.connection = null;
    this.connectionId = null;
    this.userId = null;
    this.currentRoomId = null;
    this.connectionState = HubConnectionState.Disconnected;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 5000;
    this.authToken = null;
    this.heartbeatInterval = null;
    this.isAuthenticated = false;
    
    // 事件监听器
    this.messageListeners = new Set();
    this.connectionListeners = new Set();
    this.errorListeners = new Set();
    this.userJoinedListeners = new Set();
    this.userLeftListeners = new Set();
    this.typingListeners = new Set();
    this.matchingListeners = new Set();
  }

  // 初始化 SignalR 连接
  async initializeConnection(userId, authToken = null) {
    try {
      this.userId = userId;
      this.authToken = authToken;

      // 构建连接
      const connectionBuilder = new HubConnectionBuilder()
        .withUrl(SIGNALR_HUB_URL, {
          accessTokenFactory: () => getToken(), // 這裡提供 JWT token
          withCredentials: false, // JWT 認證時通常設為 false
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        })
        .withHubProtocol(new MessagePackHubProtocol())
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: retryContext => {
            if (retryContext.previousRetryCount < 3) {
              return Math.random() * 10000;
            } else {
              return 30000;
            }
          }
        })
        .configureLogging(LogLevel.Information)
        .build();

      this.connection = connectionBuilder;
      
      // 设置事件监听器
      this.setupEventListeners();
      
      // 启动连接
      await this.startConnection();
      
      return true;
    } catch (error) {
      console.error('SignalR connection initialization failed:', error);
      this.notifyErrorListeners('连接初始化失败', error);
      return false;
    }
  }

  // 设置事件监听器
  setupEventListeners() {
    if (!this.connection) return;

    // 连接状态事件
    this.connection.onclose((error) => {
      this.connectionState = HubConnectionState.Disconnected;
      this.isAuthenticated = false;
      this.stopHeartbeat();
      console.warn('SignalR connection closed:', error);
      this.notifyConnectionListeners('disconnected', error);
    });

    this.connection.onreconnecting((error) => {
      this.connectionState = HubConnectionState.Reconnecting;
      console.warn('SignalR reconnecting:', error);
      this.notifyConnectionListeners('reconnecting', error);
    });

    this.connection.onreconnected((connectionId) => {
      this.connectionId = connectionId;
      this.connectionState = HubConnectionState.Connected;
      this.reconnectAttempts = 0;
      console.log('SignalR reconnected:', connectionId);
      this.notifyConnectionListeners('reconnected', connectionId);
      this.startHeartbeat();
      
      // 重新加入当前房间
      if (this.currentRoomId) {
        this.joinRoom(this.currentRoomId);
      }
    });

    // 业务事件监听
    this.connection.on('ReceiveMessage', (message) => {
      this.validateAndProcessMessage(message);
    });

    this.connection.on('UserJoined', (user) => {
      this.notifyUserJoinedListeners(user);
    });

    this.connection.on('UserLeft', (user) => {
      this.notifyUserLeftListeners(user);
    });

    this.connection.on('UserTyping', (userId, isTyping) => {
      this.notifyTypingListeners(userId, isTyping);
    });

    this.connection.on('MatchingResult', (result) => {
      this.notifyMatchingListeners(result);
    });

    this.connection.on('RoomExpired', (roomId) => {
      if (this.currentRoomId === roomId) {
        this.currentRoomId = null;
      }
      this.notifyErrorListeners('房间已过期', { roomId });
    });

    this.connection.on('AuthenticationRequired', () => {
      this.isAuthenticated = false;
      this.notifyErrorListeners('需要重新认证', null);
    });

    this.connection.on('SecurityViolation', (reason) => {
      this.notifyErrorListeners('安全违规', { reason });
      this.disconnect();
    });
  }

  // 启动连接
  async startConnection() {
    try {
      if (this.connection.state === HubConnectionState.Disconnected) {
        await this.connection.start();
        this.connectionId = this.connection.connectionId;
        this.connectionState = this.connection.state;
        
        console.log('SignalR connected:', this.connectionId);
        this.notifyConnectionListeners('connected', this.connectionId);
        
        // 启动心跳
        this.startHeartbeat();
        
        // 用户认证
        await this.authenticateUser();
        
        return true;
      }
    } catch (error) {
      console.error('SignalR connection failed:', error);
      this.notifyErrorListeners('连接失败', error);
      
      // 实现重连机制
      await this.handleReconnect();
      return false;
    }
  }

  // 用户认证 - 使用 JWT 時通常不需要額外的認證步驟
  async authenticateUser() {
    try {
      if (!this.userId) {
        throw new Error('User ID is required for authentication');
      }

      // 使用 JWT 認證時，token 已經在連接時自動發送
      // 只需要驗證連接狀態即可
      if (this.connection && this.connection.state === HubConnectionState.Connected) {
        this.isAuthenticated = true;
        return;
      }

      // 如果後端仍需要額外的認證步驟，可以這樣調用：
      try {
        const result = await this.connection.invoke('AuthenticateUser');
        
        if (result && result.success) {
          this.isAuthenticated = true;
          console.log('User authenticated successfully');
        } else {
          this.isAuthenticated = false;
          console.warn('Authentication failed:', result?.message || '認證失败');
        }
      } catch (authError) {
        // 如果 AuthenticateUser 方法不存在或不需要，這是正常的
        console.log('No additional authentication required, using JWT token');
        this.isAuthenticated = true;
      }

    } catch (error) {
      console.error('Authentication failed:', error);
      this.isAuthenticated = false;
      this.notifyErrorListeners('用户认证失败', error);
    }
  }

  // 心跳机制
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(async () => {
      try {
        if (this.connection.state === HubConnectionState.Connected) {
          await this.connection.invoke('Heartbeat');
        }
      } catch (error) {
        console.warn('Heartbeat failed:', error);
      }
    }, 30000); // 每30秒发送心跳
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // 重连机制
  async handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.notifyErrorListeners('超过最大重连次数', null);
      return false;
    }

    this.reconnectAttempts++;
    console.log(`Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
    
    await new Promise(resolve => setTimeout(resolve, this.reconnectDelay));
    
    try {
      await this.startConnection();
      return true;
    } catch (error) {
      console.error('Reconnection failed:', error);
      return this.handleReconnect();
    }
  }

  // 消息验证和处理
  validateAndProcessMessage(message) {
    try {
      // 基本验证
      if (!message || !message.content) {
        console.warn('Invalid message received:', message);
        return;
      }

      // 安全检查
      if (message.content.length > 5000) {
        console.warn('Message too long, ignored:', message.id);
        return;
      }

      // XSS 防护
      const sanitizedMessage = {
        ...message,
        content: this.sanitizeContent(message.content)
      };

      this.notifyMessageListeners(sanitizedMessage);
    } catch (error) {
      console.error('Message validation failed:', error);
      this.notifyErrorListeners('消息验证失败', error);
    }
  }

  // 内容清理
  sanitizeContent(content) {
    // 简单的 XSS 防护
    return content
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }

  // 等待连接就绪
  async waitForConnection(timeoutMs = 10000) {
    const startTime = Date.now();
    
    while (Date.now() - startTime < timeoutMs) {
      if (this.connection && 
          this.connection.state === HubConnectionState.Connected && 
          this.isAuthenticated) {
        return true;
      }
      
      if (this.connection && this.connection.state === HubConnectionState.Disconnected) {
        throw new Error('Connection is disconnected');
      }
      
      // 等待一小段时间再检查
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    throw new Error(`Connection timeout after ${timeoutMs}ms`);
  }

  // 检查连接状态
  isConnectionReady() {
    return this.connection && 
           this.connection.state === HubConnectionState.Connected && 
           this.isAuthenticated;
  }

  // 加入房间
  async joinRoom(roomId) {
    try {
      // 检查基本认证状态
      if (!this.isAuthenticated) {
        throw new Error('User not authenticated');
      }

      // 等待连接就绪
      if (!this.isConnectionReady()) {
        console.log('Connection not ready, waiting...');
        await this.waitForConnection();
      }

      const result = await this.connection.invoke('JoinRoom', roomId);

      console.log('joinRoom result:', result);

      if (result.success) {
        this.currentRoomId = roomId;
        console.log('Successfully joined room:', roomId);
        return true;
      } else {
        throw new Error(result.message || '加入房间失败');
      }
    } catch (error) {
      console.error('Failed to join room:', error);
      this.notifyErrorListeners('加入房间失败', error);
      return false;
    }
  }

  // 离开房间
  async leaveRoom(roomId) {
    try {
      // 检查基本认证状态
      if (!this.isAuthenticated) {
        throw new Error('User not authenticated');
      }

      // 等待连接就绪
      if (!this.isConnectionReady()) {
        console.log('Connection not ready, waiting...');
        await this.waitForConnection();
      }

      const result = await this.connection.invoke('LeaveRoom', roomId, this.userId, Date.now());

      if (result.success) {
        if (this.currentRoomId === roomId) {
          this.currentRoomId = null;
        }
        console.log('Successfully left room:', roomId);
        return true;
      } else {
        throw new Error(result.message || '离开房间失败');
      }
    } catch (error) {
      console.error('Failed to leave room:', error);
      this.notifyErrorListeners('离开房间失败', error);
      return false;
    }
  }

  // 发送消息
  async sendMessage(roomId, content, messageType = 'text') {
    try {
      // 检查基本认证状态
      if (!this.isAuthenticated) {
        throw new Error('User not authenticated');
      }

      if (!content || content.trim().length === 0) {
        throw new Error('Message content is required');
      }

      if (content.length > 1000) {
        throw new Error('Message too long (max 1000 characters)');
      }

      // 等待连接就绪
      if (!this.isConnectionReady()) {
        console.log('Connection not ready, waiting...');
        await this.waitForConnection();
      }

      const result = await this.connection.invoke('SendMessage', roomId, content.trim(), messageType);
      
      if (result.success) {
        console.log('Message sent successfully');
        return result.messageId;
      } else {
        throw new Error(result.message || '发送消息失败');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      this.notifyErrorListeners('发送消息失败', error);
      throw error;
    }
  }

  // 发送打字状态
  async sendTypingStatus(roomId, isTyping) {
    try {
      if (!this.isAuthenticated || !this.currentRoomId) {
        return;
      }

      // 检查连接状态，如果不ready就直接返回，不抛错误
      if (!this.isConnectionReady()) {
        console.warn('Connection not ready for typing status');
        return;
      }

      await this.connection.invoke('SendTypingStatus', roomId, this.userId, isTyping, Date.now());
    } catch (error) {
      console.warn('Failed to send typing status:', error);
    }
  }

  // 断开连接
  async disconnect() {
    try {
      this.stopHeartbeat();
      
      if (this.connection && this.connection.state === HubConnectionState.Connected) {
        await this.connection.stop();
      }
      
      this.connectionState = HubConnectionState.Disconnected;
      this.isAuthenticated = false;
      this.currentRoomId = null;
      this.connectionId = null;
      
      console.log('SignalR disconnected');
    } catch (error) {
      console.error('Disconnect failed:', error);
    }
  }

  // 事件监听器管理
  onMessage(callback) {
    this.messageListeners.add(callback);
    return () => this.messageListeners.delete(callback);
  }

  onConnection(callback) {
    this.connectionListeners.add(callback);
    return () => this.connectionListeners.delete(callback);
  }

  onError(callback) {
    this.errorListeners.add(callback);
    return () => this.errorListeners.delete(callback);
  }

  onUserJoined(callback) {
    this.userJoinedListeners.add(callback);
    return () => this.userJoinedListeners.delete(callback);
  }

  onUserLeft(callback) {
    this.userLeftListeners.add(callback);
    return () => this.userLeftListeners.delete(callback);
  }

  onTyping(callback) {
    this.typingListeners.add(callback);
    return () => this.typingListeners.delete(callback);
  }

  onMatching(callback) {
    this.matchingListeners.add(callback);
    return () => this.matchingListeners.delete(callback);
  }

  // 通知监听器
  notifyMessageListeners(message) {
    this.messageListeners.forEach(callback => {
      try {
        callback(message);
      } catch (error) {
        console.error('Message listener error:', error);
      }
    });
  }

  notifyConnectionListeners(status, data) {
    this.connectionListeners.forEach(callback => {
      try {
        callback(status, data);
      } catch (error) {
        console.error('Connection listener error:', error);
      }
    });
  }

  notifyErrorListeners(message, error) {
    this.errorListeners.forEach(callback => {
      try {
        callback(message, error);
      } catch (error) {
        console.error('Error listener error:', error);
      }
    });
  }

  notifyUserJoinedListeners(user) {
    this.userJoinedListeners.forEach(callback => {
      try {
        callback(user);
      } catch (error) {
        console.error('User joined listener error:', error);
      }
    });
  }

  notifyUserLeftListeners(user) {
    this.userLeftListeners.forEach(callback => {
      try {
        callback(user);
      } catch (error) {
        console.error('User left listener error:', error);
      }
    });
  }

  notifyTypingListeners(userId, isTyping) {
    this.typingListeners.forEach(callback => {
      try {
        callback(userId, isTyping);
      } catch (error) {
        console.error('Typing listener error:', error);
      }
    });
  }

  notifyMatchingListeners(result) {
    this.matchingListeners.forEach(callback => {
      try {
        callback(result);
      } catch (error) {
        console.error('Matching listener error:', error);
      }
    });
  }

  // 获取连接状态
  getConnectionState() {
    return {
      state: this.connectionState,
      isAuthenticated: this.isAuthenticated,
      connectionId: this.connectionId,
      currentRoomId: this.currentRoomId,
      userId: this.userId
    };
  }
}

// 创建全局 SignalR 管理器实例
const signalRManager = new SignalRManager();

// 用户相关 API
export const saveUserProfile = async (userData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/User/nomatch/guest-login`, userData);
    return response.data;
  } catch (error) {
    console.error('Error saving user profile:', error);
    throw error;
  }
};

export const getUserProfile = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/User/nomatch/guest-profile`);
    return response.data;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

// 匹配相关 API
export const requestMatching = async () => {
  try {
    const response = await axios.post(`${API_BASE_URL}/Chat/nomatch/matching/request`);
    return response.data;
  } catch (error) {
    console.error('Error requesting matching:', error);
    throw error;
  }
};

// 聊天室相关 API
export const getChatRooms = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Chat/nomatch/chatrooms`);
    return response.data;
  } catch (error) {
    console.error('Error getting chat rooms:', error);
    throw error;
  }
};

export const getChatRoom = async (roomId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Chat/nomatch/chatrooms/${roomId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting chat room:', error);
    throw error;
  }
};

export const leaveChatRoom = async (roomId) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/Chat/nomatch/chatrooms/${roomId}/leave`);
    return response.data;
  } catch (error) {
    console.error('Error leaving chat room:', error);
    throw error;
  }
};

// 消息相关 API
export const getChatMessages = async (roomId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Chat/nomatch/chatrooms/${roomId}/messages`);
    return response.data;
  } catch (error) {
    console.error('Error getting chat messages:', error);
    throw error;
  }
};

export const sendMessage = async (roomId, message) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/Chat/nomatch/chatrooms/${roomId}/messages`, { message });
    return response.data;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// SignalR 相关 API
export const initializeSignalR = async (userId, authToken = null) => {
  return await signalRManager.initializeConnection(userId, authToken);
};

export const disconnectSignalR = async () => {
  return await signalRManager.disconnect();
};

export const joinChatRoom = async (roomId) => {
  return await signalRManager.joinRoom(roomId);
};

export const leaveChatRoomSignalR = async (roomId) => {
  return await signalRManager.leaveRoom(roomId);
};

export const sendMessageSignalR = async (roomId, content, messageType = 'text') => {
  return await signalRManager.sendMessage(roomId, content, messageType);
};

export const sendTypingStatus = async (roomId, isTyping) => {
  return await signalRManager.sendTypingStatus(roomId, isTyping);
};

export const getSignalRConnectionState = () => {
  return signalRManager.getConnectionState();
};

// 事件监听器
export const onMessage = (callback) => {
  return signalRManager.onMessage(callback);
};

export const onConnection = (callback) => {
  return signalRManager.onConnection(callback);
};

export const onError = (callback) => {
  return signalRManager.onError(callback);
};

export const onUserJoined = (callback) => {
  return signalRManager.onUserJoined(callback);
};

export const onUserLeft = (callback) => {
  return signalRManager.onUserLeft(callback);
};

export const onTyping = (callback) => {
  return signalRManager.onTyping(callback);
};

export const onMatching = (callback) => {
  return signalRManager.onMatching(callback);
};

// 导出 SignalR 管理器实例（用于高级用法）
export { signalRManager };